import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CloudOutlined,
  EditOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  RedoOutlined,
  FileTextOutlined,
  CameraOutlined,
  HistoryOutlined,
  DownloadOutlined,
  LineChartOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import { GcloudLinkButton } from "../../o11y/GcloudLinkButton";
import { controllerLogsUrl, controllerTracesUrl } from "../../../util/gcloud";
import { downloadJson } from "../../../util/downloadJson";
import { stripVersionPrefix } from "../../../util/controllerVersion";
import { useIsViewer } from "../../../hooks/useIsViewer";
import { DisableForViewer } from "../../common/DisableForViewer";
import {
  claimControllerMutation,
  listControllersOptions,
  listControllersQueryKey,
  listUnclaimedControllersOptions,
  listUnclaimedControllersQueryKey,
  reloadAndRestartControllerMutation,
  reloadControllerConfigurationMutation,
  restartControllerMutation,
  setControllerLogLevelMutation,
  setControllerVersionMutation,
  unclaimControllerMutation,
  updateControllerStaticIpMutation,
} from "../../../services/licensing/client/@tanstack/react-query.gen";
import {
  getControllerSnapshot,
  getControllerSnapshotAtAge,
  getControllerSnapshotHistory,
} from "../../../services/licensing/client/sdk.gen";
import type {
  Controller,
  StaticIpConfiguration,
} from "../../../services/licensing/client/types.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { ControllerConfigurationCard } from "./ControllerConfigurationCard";

type ControllerTabProps = {
  site_id: string;
};

const extractErrorMessage = (error: unknown): string => {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Please try again.";
};

type LogLevelFormValues = { filter: string };

type SnapshotAgeFormValues = { age: string };

const metricsFrontendUrl = (hostname?: string | null) =>
  hostname ? `http://${hostname}:9000` : undefined;

const unclaimConfirmationText = (controller: Controller) =>
  controller.device_info?.serial_numbers?.hostname ?? controller._id ?? "";

export const ControllerTab: React.FC<ControllerTabProps> = ({ site_id }) => {
  const isViewer = useIsViewer();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [staticIpForm] = Form.useForm<StaticIpConfiguration>();
  const [logLevelForm] = Form.useForm<LogLevelFormValues>();
  const [snapshotAgeForm] = Form.useForm<SnapshotAgeFormValues>();

  // Controller currently having its target version edited inline.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [versionDraft, setVersionDraft] = useState("");
  // Controller whose static IP modal is open.
  const [staticIpEditId, setStaticIpEditId] = useState<string | null>(null);
  // Controller whose log-level modal is open.
  const [logLevelId, setLogLevelId] = useState<string | null>(null);
  // Controller whose "snapshot at age" modal is open.
  const [snapshotAgeController, setSnapshotAgeController] =
    useState<Controller | null>(null);
  // Which snapshot download is in flight, keyed as `${controller_id}:${kind}`.
  const [snapshotBusy, setSnapshotBusy] = useState<string | null>(null);
  // Controller whose unclaim confirmation modal is open.
  const [unclaimingController, setUnclaimingController] =
    useState<Controller | null>(null);
  const [unclaimConfirmation, setUnclaimConfirmation] = useState("");

  const {
    data: controllers,
    isLoading,
    error,
  } = useQuery(listControllersOptions());

  const { data: unclaimedControllers, isLoading: isLoadingUnclaimed } =
    useQuery(listUnclaimedControllersOptions());

  const claimedForSite = useMemo(
    () => (controllers ?? []).filter((c) => c.site_id === site_id),
    [controllers, site_id],
  );

  const staticIpController = claimedForSite.find(
    (c) => c._id === staticIpEditId,
  );

  const claimMutation = useMutation({
    ...claimControllerMutation(),
    onSuccess: (controller) => {
      queryClient.invalidateQueries({ queryKey: listControllersQueryKey() });
      queryClient.invalidateQueries({
        queryKey: listUnclaimedControllersQueryKey(),
      });
      form.resetFields();
      notify({
        type: "success",
        message: "Controller claimed",
        description: `Controller ${controller._id ?? ""} is now claimed for this site.`,
      });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to claim controller",
        description: extractErrorMessage(err),
      });
    },
  });

  const unclaimMutation = useMutation({
    ...unclaimControllerMutation(),
    onSuccess: (controller) => {
      queryClient.invalidateQueries({ queryKey: listControllersQueryKey() });
      queryClient.invalidateQueries({
        queryKey: listUnclaimedControllersQueryKey(),
      });
      setUnclaimingController(null);
      notify({
        type: "success",
        message: "Controller unclaimed",
        description: `Controller ${controller._id ?? ""} is no longer claimed for this site.`,
      });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to unclaim controller",
        description: extractErrorMessage(err),
      });
    },
  });

  const openUnclaim = (controller: Controller) => {
    setUnclaimConfirmation("");
    setUnclaimingController(controller);
  };

  const isUnclaimConfirmed =
    !!unclaimingController &&
    unclaimConfirmation.trim() ===
      unclaimConfirmationText(unclaimingController);

  const confirmUnclaim = () => {
    if (
      !unclaimingController ||
      !isUnclaimConfirmed ||
      unclaimMutation.isPending
    ) {
      return;
    }
    unclaimMutation.mutate({
      path: { controller_id: unclaimingController._id ?? "" },
      body: { site_id },
    });
  };

  const versionMutation = useMutation({
    ...setControllerVersionMutation(),
    onSuccess: (controller) => {
      queryClient.invalidateQueries({ queryKey: listControllersQueryKey() });
      setEditingId(null);
      notify({
        type: "success",
        message: "Target version updated",
        description: `Controller ${controller._id ?? ""} is now set to version ${controller.target_version ?? ""}.`,
      });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to update target version",
        description: extractErrorMessage(err),
      });
    },
  });

  const staticIpMutation = useMutation({
    ...updateControllerStaticIpMutation(),
    onSuccess: (controller) => {
      queryClient.invalidateQueries({ queryKey: listControllersQueryKey() });
      setStaticIpEditId(null);
      notify({
        type: "success",
        message: "Static IP updated",
        description: `Static IP configuration for controller ${controller._id ?? ""} has been updated.`,
      });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to update static IP",
        description: extractErrorMessage(err),
      });
    },
  });

  // Admin actions forwarded to the controller over the Tailnet via the
  // controller-proxy service. They don't change the Controller document, so
  // they only surface a notification rather than invalidating the list.
  const actionHandlers = (label: string) => ({
    onSuccess: () => notify({ type: "success", message: `${label} triggered` }),
    onError: (err: unknown) =>
      notify({
        type: "error",
        message: `Failed to ${label.toLowerCase()}`,
        description: extractErrorMessage(err),
      }),
  });

  const reloadConfigMutation = useMutation({
    ...reloadControllerConfigurationMutation(),
    ...actionHandlers("Reload configuration"),
  });
  const restartMutation = useMutation({
    ...restartControllerMutation(),
    ...actionHandlers("Restart"),
  });
  const reloadAndRestartMutation = useMutation({
    ...reloadAndRestartControllerMutation(),
    ...actionHandlers("Reload & restart"),
  });

  const logLevelMutation = useMutation({
    ...setControllerLogLevelMutation(),
    onSuccess: (result) => {
      setLogLevelId(null);
      logLevelForm.resetFields();
      notify({
        type: "success",
        message: "Log level set",
        description: result || undefined,
      });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to set log level",
        description: extractErrorMessage(err),
      });
    },
  });

  const onFinish = (values: { controller_id: string }) => {
    claimMutation.mutate({
      path: { controller_id: values.controller_id.trim() },
      body: { site_id },
    });
  };

  const targetVersion = stripVersionPrefix(versionDraft);

  const saveVersion = () => {
    if (!editingId || !targetVersion) {
      return;
    }
    versionMutation.mutate({
      path: { controller_id: editingId },
      body: { version: targetVersion },
    });
  };

  const saveStaticIp = (values: StaticIpConfiguration) => {
    if (!staticIpEditId) {
      return;
    }
    staticIpMutation.mutate({
      path: { controller_id: staticIpEditId },
      // A cleared gateway input submits "", which the API rejects as an IPv4 address.
      body: {
        static_ip: { ...values, gateway: values.gateway?.trim() || null },
      },
    });
  };

  const removeStaticIp = () => {
    if (!staticIpEditId) {
      return;
    }
    staticIpMutation.mutate({
      path: { controller_id: staticIpEditId },
      body: { static_ip: null },
    });
  };

  const submitLogLevel = (values: LogLevelFormValues) => {
    if (!logLevelId) {
      return;
    }
    logLevelMutation.mutate({
      path: { controller_id: logLevelId },
      body: { filter: values.filter.trim() },
    });
  };

  const downloadSnapshot = async (
    controller: Controller,
    kind: "now" | "history" | "age",
    age?: string,
  ): Promise<boolean> => {
    const controllerId = controller._id;
    if (!controllerId) {
      return false;
    }
    const busyKey = `${controllerId}:${kind}`;
    setSnapshotBusy(busyKey);
    try {
      const filenameSuffix =
        kind === "now"
          ? "snapshot"
          : kind === "history"
            ? "snapshot_history"
            : `snapshot_${age}`;
      // Read as text rather than parsed JSON: a snapshot history runs to tens of
      // megabytes, and parsing it only to re-serialize it for the download is wasted.
      const { data } =
        kind === "now"
          ? await getControllerSnapshot({
              path: { controller_id: controllerId },
              parseAs: "text",
              throwOnError: true,
            })
          : kind === "history"
            ? await getControllerSnapshotHistory({
                path: { controller_id: controllerId },
                parseAs: "text",
                throwOnError: true,
              })
            : await getControllerSnapshotAtAge({
                path: { controller_id: controllerId, age: age ?? "" },
                parseAs: "text",
                throwOnError: true,
              });
      downloadJson(
        `${process.env.NODE_ENV}-controller_${filenameSuffix}-${site_id}-${controllerId}.json`,
        data,
      );
      notify({ type: "success", message: "Snapshot downloaded" });
      return true;
    } catch (err) {
      notify({
        type: "error",
        message: "Failed to download snapshot",
        description: extractErrorMessage(err),
      });
      return false;
    } finally {
      setSnapshotBusy(null);
    }
  };

  // Restart actions always capture the full snapshot history first; the restart
  // is only sent once that snapshot has been downloaded successfully.
  const snapshotThenRestart = async (
    controller: Controller,
    restart: () => void,
  ) => {
    if (await downloadSnapshot(controller, "history")) {
      restart();
    }
  };

  const submitSnapshotAge = (values: SnapshotAgeFormValues) => {
    if (!snapshotAgeController) {
      return;
    }
    const controller = snapshotAgeController;
    setSnapshotAgeController(null);
    snapshotAgeForm.resetFields();
    void downloadSnapshot(controller, "age", values.age.trim());
  };

  return (
    <div className="w-full">
      <Typography.Paragraph type="secondary" className="mb-4">
        Claim a controller to this site by selecting an unclaimed controller.
        All controllers claimed for this site are listed below.
      </Typography.Paragraph>

      <Space wrap className="mb-6">
        <GcloudLinkButton href={controllerLogsUrl(site_id)}>
          Logs
        </GcloudLinkButton>
        <GcloudLinkButton href={controllerTracesUrl(site_id)}>
          Traces
        </GcloudLinkButton>
        <Button icon={<CloudOutlined />} disabled title="Coming soon">
          Metrics (TODO)
        </Button>
      </Space>

      <ControllerConfigurationCard site_id={site_id} />

      <Form
        form={form}
        layout="inline"
        onFinish={onFinish}
        className="mb-6"
        disabled={isViewer}
      >
        <Form.Item
          name="controller_id"
          label="Controller"
          rules={[{ required: true, message: "Please select a controller" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={isLoadingUnclaimed}
            placeholder="Select an unclaimed controller"
            style={{ width: 420 }}
            options={(unclaimedControllers ?? []).map((controller) => ({
              value: controller._id ?? "",
              label: `${controller.device_info?.serial_numbers?.hostname ?? "unknown"} (${controller._id ?? ""})`,
            }))}
            notFoundContent={
              isLoadingUnclaimed ? (
                <Spin size="small" />
              ) : (
                "No unclaimed controllers"
              )
            }
          />
        </Form.Item>
        <Form.Item>
          <DisableForViewer>
            <Button
              type="primary"
              htmlType="submit"
              loading={claimMutation.isPending}
            >
              Claim
            </Button>
          </DisableForViewer>
        </Form.Item>
      </Form>

      {!!error && (
        <Alert
          type="error"
          showIcon
          message="Failed to load controllers"
          description={extractErrorMessage(error)}
          className="mb-4"
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : claimedForSite.length === 0 ? (
        <Empty description="No controllers claimed for this site yet" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {claimedForSite.map((controller) => (
            <Card
              key={controller._id}
              size="small"
              title={controller.device_info?.model_name ?? "Controller"}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Controller ID">
                  {controller._id ?? "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Hostname">
                  {controller.device_info?.serial_numbers?.hostname ?? "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Static IP">
                  <Space>
                    {controller.static_ip ? (
                      <Tag color="green">{controller.static_ip.address}</Tag>
                    ) : (
                      <Tag>Not configured</Tag>
                    )}
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      title="Configure static IP"
                      onClick={() => setStaticIpEditId(controller._id ?? null)}
                    />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Target Version">
                  {editingId === controller._id ? (
                    <Space.Compact>
                      <Input
                        size="small"
                        style={{ width: 140 }}
                        value={versionDraft}
                        placeholder="e.g. 1.2.3"
                        autoFocus
                        onChange={(e) => setVersionDraft(e.target.value)}
                        onPressEnter={saveVersion}
                      />
                      <DisableForViewer>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={versionMutation.isPending}
                          disabled={!targetVersion}
                          onClick={saveVersion}
                        />
                      </DisableForViewer>
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        disabled={versionMutation.isPending}
                        onClick={() => setEditingId(null)}
                      />
                    </Space.Compact>
                  ) : (
                    <Space>
                      <span>{controller.target_version ?? "-"}</span>
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        title="Edit target version"
                        onClick={() => {
                          setEditingId(controller._id ?? null);
                          setVersionDraft(controller.target_version ?? "");
                        }}
                      />
                    </Space>
                  )}
                </Descriptions.Item>
              </Descriptions>

              <Typography.Text type="secondary" className="block mb-2">
                Admin actions
              </Typography.Text>
              <Space wrap>
                <DisableForViewer>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={
                      reloadConfigMutation.isPending &&
                      reloadConfigMutation.variables?.path.controller_id ===
                        controller._id
                    }
                    onClick={() =>
                      reloadConfigMutation.mutate({
                        path: { controller_id: controller._id ?? "" },
                      })
                    }
                  >
                    Reload config
                  </Button>
                </DisableForViewer>
                <Popconfirm
                  title="Download the full snapshot history, then reload configuration and restart this controller?"
                  okText="Reload & restart"
                  onConfirm={() =>
                    void snapshotThenRestart(controller, () =>
                      reloadAndRestartMutation.mutate({
                        path: { controller_id: controller._id ?? "" },
                      }),
                    )
                  }
                >
                  <DisableForViewer>
                    <Button
                      size="small"
                      icon={<RedoOutlined />}
                      loading={
                        snapshotBusy === `${controller._id}:history` ||
                        (reloadAndRestartMutation.isPending &&
                          reloadAndRestartMutation.variables?.path
                            .controller_id === controller._id)
                      }
                    >
                      Reload &amp; restart
                    </Button>
                  </DisableForViewer>
                </Popconfirm>
                <Popconfirm
                  title="Download the full snapshot history, then restart this controller?"
                  okText="Restart"
                  okButtonProps={{ danger: true }}
                  onConfirm={() =>
                    void snapshotThenRestart(controller, () =>
                      restartMutation.mutate({
                        path: { controller_id: controller._id ?? "" },
                      }),
                    )
                  }
                >
                  <DisableForViewer>
                    <Button
                      size="small"
                      danger
                      icon={<PoweroffOutlined />}
                      loading={
                        snapshotBusy === `${controller._id}:history` ||
                        (restartMutation.isPending &&
                          restartMutation.variables?.path.controller_id ===
                            controller._id)
                      }
                    >
                      Restart
                    </Button>
                  </DisableForViewer>
                </Popconfirm>
              </Space>

              <Typography.Text type="secondary" className="block mt-3 mb-2">
                Diagnostics
              </Typography.Text>
              <Space wrap>
                <Button
                  size="small"
                  icon={<FileTextOutlined />}
                  onClick={() => {
                    logLevelForm.resetFields();
                    setLogLevelId(controller._id ?? null);
                  }}
                >
                  Log level
                </Button>
                <Button
                  size="small"
                  icon={<LineChartOutlined />}
                  disabled={!controller.device_info?.serial_numbers?.hostname}
                  href={metricsFrontendUrl(
                    controller.device_info?.serial_numbers?.hostname,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Metrics Frontend
                </Button>
              </Space>

              <Typography.Text type="secondary" className="block mt-3 mb-2">
                Snapshots
              </Typography.Text>
              <Space wrap>
                <Button
                  size="small"
                  icon={<CameraOutlined />}
                  loading={snapshotBusy === `${controller._id}:now`}
                  onClick={() => downloadSnapshot(controller, "now")}
                >
                  Snapshot now
                </Button>
                <Button
                  size="small"
                  icon={<HistoryOutlined />}
                  onClick={() => {
                    snapshotAgeForm.resetFields();
                    setSnapshotAgeController(controller);
                  }}
                >
                  Snapshot at age…
                </Button>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={snapshotBusy === `${controller._id}:history`}
                  onClick={() => downloadSnapshot(controller, "history")}
                >
                  Export history
                </Button>
              </Space>

              <Typography.Text type="danger" className="block mt-3 mb-2">
                Danger zone
              </Typography.Text>
              <DisableForViewer>
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={() => openUnclaim(controller)}
                >
                  Unclaim from site
                </Button>
              </DisableForViewer>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title="Configure static IP"
        open={!!staticIpController}
        onCancel={() => setStaticIpEditId(null)}
        destroyOnClose
        footer={[
          staticIpController?.static_ip ? (
            <DisableForViewer key="remove">
              <Button
                danger
                loading={staticIpMutation.isPending}
                onClick={removeStaticIp}
              >
                Remove
              </Button>
            </DisableForViewer>
          ) : null,
          <Button
            key="cancel"
            disabled={staticIpMutation.isPending}
            onClick={() => setStaticIpEditId(null)}
          >
            Cancel
          </Button>,
          <DisableForViewer key="save">
            <Button
              type="primary"
              loading={staticIpMutation.isPending}
              onClick={() => staticIpForm.submit()}
            >
              Save
            </Button>
          </DisableForViewer>,
        ]}
      >
        {staticIpController && (
          <Form
            key={staticIpController._id}
            form={staticIpForm}
            layout="vertical"
            onFinish={saveStaticIp}
            disabled={isViewer}
            requiredMark="optional"
            initialValues={{
              interface: staticIpController.static_ip?.interface ?? "",
              address: staticIpController.static_ip?.address ?? "",
              gateway: staticIpController.static_ip?.gateway ?? "",
              dns: staticIpController.static_ip?.dns ?? [],
            }}
          >
            <Form.Item
              name="interface"
              label="Interface"
              rules={[{ required: true, message: "Please enter an interface" }]}
            >
              <Input placeholder="eth0" />
            </Form.Item>
            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: "Please enter an address" }]}
            >
              <Input placeholder="192.168.1.50/24" />
            </Form.Item>
            <Form.Item
              name="gateway"
              label="Gateway"
              extra="Leave empty on a local network without a router, e.g. only chargers and meters on a switch. The controller keeps reaching the internet over its other interface."
            >
              <Input placeholder="192.168.1.1" allowClear />
            </Form.Item>
            <Form.Item
              name="dns"
              label="DNS servers"
              extra="Required when a gateway is set."
              dependencies={["gateway"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, dns?: string[]) =>
                    getFieldValue("gateway")?.trim() && !dns?.length
                      ? Promise.reject(
                          new Error(
                            "Please enter at least one DNS server when a gateway is set",
                          ),
                        )
                      : Promise.resolve(),
                }),
              ]}
            >
              <Select
                mode="tags"
                open={false}
                tokenSeparators={[","]}
                placeholder="Add DNS server(s)"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Unclaim controller from this site?"
        open={!!unclaimingController}
        onCancel={() => setUnclaimingController(null)}
        destroyOnClose
        okText="Unclaim controller"
        okButtonProps={{
          danger: true,
          disabled: isViewer || !isUnclaimConfirmed,
        }}
        confirmLoading={unclaimMutation.isPending}
        onOk={confirmUnclaim}
      >
        {unclaimingController && (
          <>
            <Alert
              type="error"
              showIcon
              className="mb-4"
              message="This removes the controller from this site"
              description={
                <ul className="list-disc pl-4 m-0">
                  <li>
                    It will no longer be deployed to: new versions, reloaded
                    configuration and static IP changes will not reach it.
                  </li>
                  <li>
                    A device that is already running is <b>not</b> stopped. It
                    keeps controlling this site with its current license and
                    credentials until the device is reset.
                  </li>
                  <li>
                    Before claiming it for another site, reset the device so it
                    does not keep this site&apos;s license and credentials.
                  </li>
                </ul>
              }
            />
            <Typography.Paragraph>
              Type{" "}
              <Typography.Text code copyable>
                {unclaimConfirmationText(unclaimingController)}
              </Typography.Text>{" "}
              to confirm.
            </Typography.Paragraph>
            <Input
              autoFocus
              value={unclaimConfirmation}
              placeholder={unclaimConfirmationText(unclaimingController)}
              onChange={(e) => setUnclaimConfirmation(e.target.value)}
              onPressEnter={confirmUnclaim}
              status={
                unclaimConfirmation && !isUnclaimConfirmed ? "error" : undefined
              }
            />
          </>
        )}
      </Modal>

      <Modal
        title="Set log level"
        open={!!logLevelId}
        onCancel={() => setLogLevelId(null)}
        destroyOnClose
        okText="Apply"
        confirmLoading={logLevelMutation.isPending}
        onOk={() => logLevelForm.submit()}
        okButtonProps={{ disabled: isViewer }}
      >
        <Form
          form={logLevelForm}
          layout="vertical"
          onFinish={submitLogLevel}
          disabled={isViewer}
        >
          <Form.Item
            name="filter"
            label="Filter"
            rules={[{ required: true, message: "Please enter a filter" }]}
            extra="EnvFilter / RUST_LOG syntax, e.g. info,controller=trace. Reverts automatically after 15 minutes."
          >
            <Input placeholder="info,controller=trace" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Snapshot at a point in history"
        open={!!snapshotAgeController}
        onCancel={() => setSnapshotAgeController(null)}
        destroyOnClose
        okText="Download"
        confirmLoading={snapshotBusy?.endsWith(":age")}
        onOk={() => snapshotAgeForm.submit()}
      >
        <Form
          form={snapshotAgeForm}
          layout="vertical"
          onFinish={submitSnapshotAge}
          initialValues={{ age: "PT15M" }}
        >
          <Form.Item
            name="age"
            label="How long ago"
            rules={[
              { required: true, message: "Please enter an age" },
              {
                pattern: /^PT(\d+H)?(\d+M)?(\d+S)?$/,
                message: "ISO 8601 duration, e.g. PT15M or PT1H30M",
              },
            ]}
            extra="ISO 8601 duration expressing how long ago, e.g. PT15M (15 min), PT1H (1 hour). The closest retained snapshot (up to ~6 hours back) is returned."
          >
            <Input placeholder="PT15M" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
